import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Copy, Check, RefreshCw, Loader2, ShieldCheck, ShieldAlert, CheckCircle2, Trash2, Hash } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';

function AssetBlock({ packId, asset, onUpdated, atCap, resetFull }) {
  const api = useApi();
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const copy = () => {
    const parts = [asset.subject, asset.headline, asset.body, asset.cta, (asset.hashtags || []).join(' ')].filter(Boolean);
    navigator.clipboard.writeText(parts.join('\n\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success('Copied to clipboard');
  };

  const regenerate = async () => {
    if (atCap) {
      toast.error(`Daily Media Studio pack limit reached. Regeneration is temporarily unavailable until your quota resets at ${resetFull}. Regenerating an individual asset does not consume an additional pack.`);
      return;
    }
    setRegenerating(true);
    try {
      const res = await api.post(`/media-studio/packs/${packId}/assets/${asset.asset_id}/regenerate`);
      onUpdated?.(res.asset);
      toast.success(`${asset.platform_label || asset.platform} regenerated`);
    } catch (e) {
      if (e?.status === 429) {
        const reset = e?.data?.detail?.usage?.reset_at
          ? `midnight UTC (${new Date(e.data.detail.usage.reset_at).toUTCString().slice(0, 16)})`
          : 'quota reset';
        toast.error(`Daily Media Studio pack limit reached. Regeneration is temporarily unavailable until your quota resets at ${reset}. Regenerating an individual asset does not consume an additional pack.`);
      } else {
        toast.error(e.message || 'Regenerate failed');
      }
    } finally {
      setRegenerating(false);
    }
  };

  const unverified = asset?.critic?.unverified === true || asset?.critic?.passed === null;
  const passed = !unverified && asset?.critic?.passed !== false;

  return (
    <div className="rounded-lg border border-[#E8E6E1] bg-white p-4 space-y-3" data-testid={`ms-asset-${asset.platform}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge className="bg-[#1A1A1A] text-white">{asset.platform_label || asset.platform}</Badge>
          {asset?.critic?.repaired && <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-700">Auto-repaired</Badge>}
          {unverified
            ? <Badge variant="outline" className="text-[10px] border-stone-300 text-stone-500">Not reviewed</Badge>
            : passed
              ? <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700"><Check className="h-3 w-3 mr-0.5" />Passed critic</Badge>
              : <Badge variant="outline" className="text-[10px] border-red-200 text-red-700">Needs work</Badge>}
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={copy} data-testid={`ms-copy-${asset.platform}`}>
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={regenerate} disabled={regenerating || atCap} title={atCap ? 'Daily pack limit reached — regeneration temporarily unavailable' : 'Regenerate this asset'} data-testid={`ms-regen-${asset.platform}`}>
            {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>
      {asset.subject && <p className="text-xs font-semibold text-[#B89B5E]">Subject: {asset.subject}</p>}
      {asset.headline && <p className="font-semibold text-[#1A1A1A]">{asset.headline}</p>}
      {asset.body && <p className="text-sm text-[#1A1A1A]/80 whitespace-pre-line">{asset.body}</p>}
      {asset.cta && <p className="text-sm font-medium text-[#B89B5E]">→ {asset.cta}</p>}
      {(asset.hashtags || []).length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {asset.hashtags.map((h, i) => (
            <span key={i} className="text-[11px] text-stone-500 inline-flex items-center"><Hash className="h-2.5 w-2.5" />{h.replace(/^#/, '')}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PackCard({ pack, usage = null, onDeleted }) {
  const api = useApi();
  const [assets, setAssets] = useState(pack.assets || []);
  const [status, setStatus] = useState(pack.status);
  const [approving, setApproving] = useState(false);

  const atCap = usage != null && usage.remaining <= 0;
  const resetFull = usage?.reset_at
    ? `midnight UTC (${new Date(usage.reset_at).toUTCString().slice(0, 16)})`
    : 'midnight UTC';

  const updateAsset = (updated) => {
    setAssets((prev) => prev.map((a) => a.asset_id === updated.asset_id ? updated : a));
  };

  const approve = async () => {
    setApproving(true);
    try {
      await api.post(`/media-studio/packs/${pack.id}/approve`, { public_safe_confirmed: pack.public_safe });
      setStatus('approved');
      toast.success('Pack approved');
    } catch (e) {
      toast.error(e.message || 'Approve failed');
    } finally {
      setApproving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this media pack? This cannot be undone.')) return;
    try {
      await api.del(`/media-studio/packs/${pack.id}`);
      onDeleted?.(pack.id);
      toast.success('Pack deleted');
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  const verdict = pack.privacy_verdict || {};
  const isSuccessVisual = pack.kind === 'success_visual';

  return (
    <Card className="bg-[#FAF9F7] border-[#E8E6E1]" data-testid={`ms-pack-${pack.id}`}>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="text-base">{pack.topic || pack.concept || (isSuccessVisual ? 'Success Visual' : 'Media Pack')}</CardTitle>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {isSuccessVisual
                ? <Badge variant="outline" className="text-[10px] uppercase">Success Visual</Badge>
                : pack.mode && <Badge variant="outline" className="text-[10px] uppercase">{pack.mode}</Badge>}
              {status === 'approved'
                ? <Badge className="bg-emerald-600 text-white text-[10px]">Approved</Badge>
                : <Badge variant="outline" className="text-[10px]">Draft</Badge>}
              {isSuccessVisual ? (
                pack.critic && pack.critic.passed !== false
                  ? <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700"><ShieldCheck className="h-3 w-3 mr-0.5" />Critic approved</Badge>
                  : null
              ) : pack.privacy_verdict ? (
                verdict.clean
                  ? <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700"><ShieldCheck className="h-3 w-3 mr-0.5" />No leaks</Badge>
                  : <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-700"><ShieldAlert className="h-3 w-3 mr-0.5" />{verdict.redacted ? 'Redacted' : `${verdict.total_leaks} flags`}</Badge>
              ) : null}
            </div>
          </div>
          <div className="flex gap-2">
            {status !== 'approved' && !isSuccessVisual && (
              <Button size="sm" onClick={approve} disabled={approving} className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white" data-testid={`ms-approve-${pack.id}`}>
                {approving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}Approve
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={remove} data-testid={`ms-delete-${pack.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {atCap && (
          <div data-testid="ms-regen-cap-note" className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            Daily Media Studio pack limit reached. Regeneration is temporarily unavailable until your quota resets at {resetFull}. Regenerating an individual asset does not consume an additional pack.
          </div>
        )}
        {pack.hero_images && Object.keys(pack.hero_images).length > 0 ? (
          <div className="flex flex-wrap gap-3" data-testid={`ms-hero-set-${pack.id}`}>
            {Object.entries(pack.hero_images).map(([ratio, url]) => (
              <figure key={ratio} className="space-y-1">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt={`Branded ${ratio}`}
                  className="h-56 w-auto rounded-lg border border-[#E8E6E1] object-contain bg-white"
                  data-testid={`ms-hero-${ratio.replace(':', 'x')}-${pack.id}`} />
                <figcaption className="text-[10px] text-center text-muted-foreground uppercase tracking-wide">{ratio}</figcaption>
              </figure>
            ))}
          </div>
        ) : pack.hero_image_url ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${pack.hero_image_url}`} alt="Branded card"
            className="w-full max-w-sm rounded-lg border border-[#E8E6E1]" data-testid={`ms-hero-${pack.id}`} />
        ) : null}
        <div className="grid gap-3">
          {assets.map((a) => (
            <AssetBlock key={a.asset_id} packId={pack.id} asset={a} onUpdated={updateAsset} atCap={atCap} resetFull={resetFull} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
